
import React, { useState, useEffect } from 'react'
import { X, Calendar } from 'lucide-react'

interface FeriasFormProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: any) => Promise<void>
  funcionario: any
}

export const FeriasForm: React.FC<FeriasFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  funcionario
}) => {
  const [formData, setFormData] = useState({
    dataInicio: '',
    dataFim: '',
    observacoes: ''
  })
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (funcionario?.dataFerias) {
      setFormData({
        dataInicio: funcionario.dataFerias.inicio ? funcionario.dataFerias.inicio.split('T')[0] : '',
        dataFim: funcionario.dataFerias.fim ? funcionario.dataFerias.fim.split('T')[0] : '',
        observacoes: ''
      })
    } else {
      setFormData({
        dataInicio: '',
        dataFim: '',
        observacoes: ''
      })
    }
  }, [funcionario, isOpen])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      const dataInicio = new Date(formData.dataInicio)
      const dataFim = new Date(formData.dataFim)

      if (dataFim <= dataInicio) {
        alert('A data de fim deve ser posterior à data de início')
        return
      }

      await onSubmit({
        status: 'ferias',
        dataFerias: {
          inicio: dataInicio.toISOString(),
          fim: dataFim.toISOString()
        }
      })
      onClose()
    } catch (error) {
      console.error('Erro ao programar férias:', error)
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen || !funcionario) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-md">
        <div className="border-b p-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Programar Férias</h2>
              <p className="text-gray-600">{funcionario.nome}</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Data de Início *
              </label>
              <input
                type="date"
                required
                value={formData.dataInicio}
                onChange={(e) => setFormData(prev => ({ ...prev, dataInicio: e.target.value }))}
                min={new Date().toISOString().split('T')[0]}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Data de Fim *
              </label>
              <input
                type="date"
                required
                value={formData.dataFim}
                onChange={(e) => setFormData(prev => ({ ...prev, dataFim: e.target.value }))}
                min={formData.dataInicio || new Date().toISOString().split('T')[0]}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Observações
              </label>
              <textarea
                value={formData.observacoes}
                onChange={(e) => setFormData(prev => ({ ...prev, observacoes: e.target.value }))}
                rows={3}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Observações sobre as férias..."
              />
            </div>

            {formData.dataInicio && formData.dataFim && (
              <div className="bg-blue-50 p-4 rounded-md">
                <p className="text-sm text-blue-800">
                  <strong>Duração:</strong> {
                    Math.ceil((new Date(formData.dataFim).getTime() - new Date(formData.dataInicio).getTime()) / (1000 * 60 * 60 * 24))
                  } dias
                </p>
              </div>
            )}
          </div>

          <div className="border-t p-6 flex justify-end gap-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              <Calendar size={16} />
              {loading ? 'Salvando...' : 'Programar Férias'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default FeriasForm
